import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAtom, useAtomValue } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../../auth/AuthContext";
import getBuildsAtom from "../../atoms/get-builds-atom";
import {
  pinBuildId,
  pinnedBuildIdsAtom,
  unpinBuildId,
} from "../../atoms/pinned-builds-atom";
import { DateTime } from "luxon";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Android,
  Apple,
  Close,
  PushPin,
  PushPinOutlined,
  Search,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { Form, Formik, useFormikContext } from "formik";
import { boolean, object, string } from "yup";
import { atomWithReset, RESET } from "jotai/utils";
import { companyDataAtom } from "../test-run/test-run-atoms";
import FormikSwitch from "../../formikComponents/formik-switch";
import { type BuildData, type DeviceProviderSettings } from "../../utils/types";

type HandleConfirmRunTests = ({
  buildId,
  deviceProviderSettings,
}: {
  buildId: string;
  deviceProviderSettings?: DeviceProviderSettings;
}) => Promise<void>;

type HandleConfirmRedirect = ({
  buildId,
  queryParams,
}: {
  buildId: string;
  queryParams: string;
}) => Promise<void>;

const BUILD_FETCH_LIMIT = 500;

const normalizePlatform = (platform?: string): string =>
  (platform ?? "").trim().toLowerCase();

const getPlatformLabel = (platform?: string): string => {
  const normalizedPlatform = normalizePlatform(platform);
  if (normalizedPlatform === "ios") return "iOS";
  if (normalizedPlatform === "android") return "Android";
  return platform || "Unknown";
};

const getBundleKey = (build: BuildData): string => {
  const platform = normalizePlatform(build.platform) || "unknown";
  if (build.is_group === true) {
    return `__group__:${build.app_name ?? build.id}:${platform}`;
  }
  return `${build.bundle ?? build.app_name ?? build.id}:${platform}`;
};

const getBundleLabel = (build: BuildData): string => {
  if (build.is_group === true) {
    return build.app_name ?? "App Group";
  }
  return build.app_name ?? build.bundle ?? "Unknown app";
};

const getBundleSubLabel = (build: BuildData): string | undefined => {
  if (build.is_group === true) return "App Group";
  return build.bundle;
};

const getVersionKey = (build: BuildData): string =>
  build.appVersionName ?? "(no version)";

const getBuildTypeSuffix = (build: BuildData): string => {
  const isPhysicalDeviceBuild = build.appFileExtension === "ipa";
  const isIosBuild = build.platform.toLowerCase() === "ios";
  return isIosBuild
    ? isPhysicalDeviceBuild
      ? " (physical device)"
      : " (simulator)"
    : "";
};

interface BundleGroup {
  key: string;
  label: string;
  subLabel?: string;
  platform: string;
  iconUrl?: string;
  latestEpoch: number;
  builds: BuildData[];
}

const groupBuildsByBundle = (builds: BuildData[]): BundleGroup[] => {
  const map = new Map<string, BundleGroup>();
  for (const build of builds) {
    const key = getBundleKey(build);
    const existing = map.get(key);
    if (existing) {
      existing.builds.push(build);
      if (build.epoch > existing.latestEpoch) {
        existing.latestEpoch = build.epoch;
        if (build.iconUrl) existing.iconUrl = build.iconUrl;
      }
    } else {
      map.set(key, {
        key,
        label: getBundleLabel(build),
        subLabel: getBundleSubLabel(build),
        platform: normalizePlatform(build.platform),
        iconUrl: build.iconUrl,
        latestEpoch: build.epoch,
        builds: [build],
      });
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => b.latestEpoch - a.latestEpoch
  );
};

interface VersionGroup {
  key: string;
  label: string;
  latestEpoch: number;
  builds: BuildData[];
}

const groupBuildsByVersion = (builds: BuildData[]): VersionGroup[] => {
  const map = new Map<string, VersionGroup>();
  for (const build of builds) {
    const key = getVersionKey(build);
    const existing = map.get(key);
    if (existing) {
      existing.builds.push(build);
      if (build.epoch > existing.latestEpoch) existing.latestEpoch = build.epoch;
    } else {
      map.set(key, {
        key,
        label: key,
        latestEpoch: build.epoch,
        builds: [build],
      });
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => b.latestEpoch - a.latestEpoch
  );
};

const matchesSearch = (build: BuildData, query: string): boolean => {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    (build.app_name ?? "").toLowerCase().includes(q) ||
    (build.bundle ?? "").toLowerCase().includes(q) ||
    (build.appVersionName ?? "").toLowerCase().includes(q) ||
    (build.appVersionCode ?? "").toLowerCase().includes(q) ||
    build.platform.toLowerCase().includes(q)
  );
};

const SelectBuildDialog = () => {
  const { user } = useContext<any>(AuthContext);
  const {
    data: buildsData,
    isError,
    isLoading,
    refetch,
  } = useAtomValue(
    getBuildsAtom({
      organizationId: user.companyId,
      limit: BUILD_FETCH_LIMIT,
    })
  );
  const { data: pinnedIdsData } = useAtomValue(
    pinnedBuildIdsAtom({ organizationId: user.companyId })
  );
  const pinnedIds = useMemo(() => pinnedIdsData ?? [], [pinnedIdsData]);

  const [buildDialogState, setBuildDialogState] = useAtom(buildDialogStateAtom);

  const handleClose = useCallback(() => {
    setBuildDialogState(RESET);
  }, [setBuildDialogState]);

  const isCloudRunDialog = buildDialogState.type === "run";
  const isSuperUser = user.role === "superUser";
  const { data: companyData } = useAtomValue(companyDataAtom(user.companyId));

  const isBrowserStackEnabled = useMemo(() => {
    if (!companyData?.settings.displayBrowserStackIntegration) {
      return false;
    }
    const hasValidUsername =
      companyData.settings.browserStackIntegration.username.trim().length > 0;

    const hasValidAccessKey =
      companyData.settings.browserStackIntegration.accessKey.trim().length > 0;
    return hasValidUsername && hasValidAccessKey;
  }, [companyData]);

  const enablePhysicalDeviceExecutionToggle =
    companyData?.settings?.enablePhysicalDeviceExecutionToggle ?? false;

  const initialBuildId = buildsData?.[0]?.id ?? "";

  return (
    <Dialog
      maxWidth={"lg"}
      fullWidth={true}
      open={buildDialogState.open}
      onClose={handleClose}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Box flexGrow={1}>Select a build</Box>
          <Box>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <Formik
        enableReinitialize={true}
        initialValues={{
          selectedBuildId: initialBuildId,
          ...(isCloudRunDialog &&
            isBrowserStackEnabled && {
              useBrowserStack: true,
              ...(isSuperUser && {
                userGptDriverAccount: true,
              }),
            }),
          ...(enablePhysicalDeviceExecutionToggle && {
            usePhysicalDevice: false,
          }),
        }}
        validationSchema={object().shape({
          selectedBuildId: string().nonNullable(),
          useBrowserStack: boolean().nullable(),
          userGptDriverAccount: boolean().nullable(),
          ...(enablePhysicalDeviceExecutionToggle && {
            usePhysicalDevice: boolean().required(),
          }),
        })}
        onSubmit={async (values) => {
          const selectedBuild = buildsData?.find(
            (build) => build.id === values.selectedBuildId
          );
          let usePhysicalDevice = false;
          if (selectedBuild?.platform?.toLowerCase() === "ios") {
            usePhysicalDevice =
              selectedBuild.appFileExtension?.toLowerCase() === "ipa";
          } else if (enablePhysicalDeviceExecutionToggle) {
            usePhysicalDevice = values.usePhysicalDevice!;
          }
          if (isCloudRunDialog) {
            await (buildDialogState.handleConfirm as HandleConfirmRunTests)({
              buildId: values.selectedBuildId,
              ...(values.useBrowserStack && {
                deviceProviderSettings: {
                  provider: "browserstack",
                  userGptDriverAccount: values.userGptDriverAccount ?? false,
                },
              }),
              ...(usePhysicalDevice && {
                deviceProviderSettings: {
                  provider: "gptdriver",
                  usePhysicalDevice,
                },
              }),
            });
          } else {
            let queryParams = "";
            if (usePhysicalDevice) {
              queryParams = `?usePhysicalDevice=true`;
            }
            await (buildDialogState.handleConfirm as HandleConfirmRedirect)({
              buildId: values.selectedBuildId,
              queryParams,
            });
          }
          handleClose();
        }}
      >
        {({ isSubmitting, values, setValues }) => (
          <Form>
            <DialogContent dividers className={"flex flex-col gap-3.5"}>
              {isLoading && <Skeleton variant="rounded" height={400} />}
              {isError && (
                <Alert
                  severity={"error"}
                  action={
                    <Button color="inherit" size="small">
                      RETRY
                    </Button>
                  }
                  onClick={() => {
                    refetch();
                  }}
                >
                  Something went wrong while fetching builds
                </Alert>
              )}
              {!isLoading && !isError && (
                <BuildSelector
                  organizationId={user.companyId}
                  builds={buildsData ?? []}
                  pinnedIds={pinnedIds}
                />
              )}
              {isCloudRunDialog && (
                <>
                  <RunCloudAlert handleClose={handleClose} />
                  {isBrowserStackEnabled && (
                    <>
                      <FormikSwitch
                        type={"checkbox"}
                        label={"Use BrowserStack device provider"}
                        name={"useBrowserStack"}
                        key={`useBrowserStack`}
                        onChange={async (event) => {
                          const newValue = event.target.checked;

                          await setValues((prev) => ({
                            ...prev,
                            useBrowserStack: newValue,
                            ...(prev.userGptDriverAccount != null && {
                              userGptDriverAccount: newValue,
                            }),
                          }));
                        }}
                      />
                      {isSuperUser && (
                        <FormikSwitch
                          disabled={!values.useBrowserStack}
                          type={"checkbox"}
                          label={"Use GPTDriver BrowserStack account"}
                          name={"userGptDriverAccount"}
                          key={`userGptDriverAccount`}
                        />
                      )}
                    </>
                  )}
                </>
              )}
              {enablePhysicalDeviceExecutionToggle &&
                buildsData?.find(
                  (buildData) => buildData.id === values.selectedBuildId
                )?.platform === "android" && (
                  <FormikSwitch
                    type={"checkbox"}
                    label={"Use Physical Device"}
                    name={"usePhysicalDevice"}
                    key={`usePhysicalDevice`}
                    onChange={async (event) => {
                      const newValue = event.target.checked;

                      await setValues((prev) => ({
                        ...prev,
                        usePhysicalDevice: newValue,
                      }));
                    }}
                  />
                )}
            </DialogContent>
            <DialogActions>
              <Button
                variant={"contained"}
                disabled={
                  isSubmitting ||
                  isLoading ||
                  isError ||
                  !values.selectedBuildId
                }
                type={"submit"}
                loading={isSubmitting}
              >
                {buildDialogState.actionTitle}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

interface BuildSelectorProps {
  organizationId: string;
  builds: BuildData[];
  pinnedIds: string[];
}

const BuildSelector = ({
  organizationId,
  builds,
  pinnedIds,
}: BuildSelectorProps) => {
  const { values, setFieldValue } =
    useFormikContext<{ selectedBuildId: string }>();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBundleKey, setSelectedBundleKey] = useState<string | null>(
    null
  );
  const [selectedVersionKey, setSelectedVersionKey] = useState<string | null>(
    null
  );

  const buildsById = useMemo(() => {
    const m = new Map<string, BuildData>();
    for (const b of builds) m.set(b.id, b);
    return m;
  }, [builds]);

  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);

  const filteredBuilds = useMemo(() => {
    if (!searchQuery) return builds;
    return builds.filter((b) => matchesSearch(b, searchQuery));
  }, [builds, searchQuery]);

  const bundleGroups = useMemo(
    () => groupBuildsByBundle(filteredBuilds),
    [filteredBuilds]
  );

  const pinnedBuilds = useMemo(
    () =>
      pinnedIds
        .map((id) => buildsById.get(id))
        .filter((b): b is BuildData => b != null)
        .filter((b) => matchesSearch(b, searchQuery))
        .sort((a, b) => b.epoch - a.epoch),
    [pinnedIds, buildsById, searchQuery]
  );

  // Sync selected bundle/version when the selected build id changes
  useEffect(() => {
    const build = buildsById.get(values.selectedBuildId);
    if (!build) return;
    setSelectedBundleKey(getBundleKey(build));
    setSelectedVersionKey(getVersionKey(build));
  }, [values.selectedBuildId, buildsById]);

  const activeBundle = useMemo(() => {
    if (selectedBundleKey == null) return bundleGroups[0];
    return (
      bundleGroups.find((g) => g.key === selectedBundleKey) ?? bundleGroups[0]
    );
  }, [bundleGroups, selectedBundleKey]);

  const versionGroups = useMemo(
    () => (activeBundle ? groupBuildsByVersion(activeBundle.builds) : []),
    [activeBundle]
  );

  const activeVersion = useMemo(() => {
    if (selectedVersionKey == null) return versionGroups[0];
    return (
      versionGroups.find((g) => g.key === selectedVersionKey) ??
      versionGroups[0]
    );
  }, [versionGroups, selectedVersionKey]);

  const codeBuilds = useMemo(
    () =>
      activeVersion
        ? [...activeVersion.builds].sort((a, b) => b.epoch - a.epoch)
        : [],
    [activeVersion]
  );

  const handlePickBundle = (group: BundleGroup) => {
    setSelectedBundleKey(group.key);
    const versions = groupBuildsByVersion(group.builds);
    const firstVersion = versions[0];
    setSelectedVersionKey(firstVersion?.key ?? null);
    const firstBuild = firstVersion?.builds[0];
    if (firstBuild) {
      void setFieldValue("selectedBuildId", firstBuild.id);
    }
  };

  const handlePickVersion = (group: VersionGroup) => {
    setSelectedVersionKey(group.key);
    const firstBuild = group.builds[0];
    if (firstBuild) {
      void setFieldValue("selectedBuildId", firstBuild.id);
    }
  };

  const handlePickPinned = (build: BuildData) => {
    void setFieldValue("selectedBuildId", build.id);
  };

  const invalidatePinned = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ["pinnedBuildIds", organizationId],
    });
  }, [queryClient, organizationId]);

  const handleTogglePin = async (build: BuildData) => {
    if (pinnedSet.has(build.id)) {
      await unpinBuildId(organizationId, build.id);
    } else {
      await pinBuildId(organizationId, build.id);
    }
    invalidatePinned();
  };

  if (builds.length === 0) {
    return <Alert severity="info">No builds available.</Alert>;
  }

  return (
    <Box className="flex flex-col gap-3">
      <TextField
        size="small"
        placeholder="Search by app, bundle, version or build number"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr 1.4fr",
          height: 380,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <ColumnContainer title="App & Platform">
          {pinnedBuilds.length > 0 && (
            <>
              <SectionHeader label="Pinned" />
              <List dense disablePadding>
                {pinnedBuilds.map((build) => (
                  <PinnedBuildRow
                    key={build.id}
                    build={build}
                    selected={values.selectedBuildId === build.id}
                    onClick={() => handlePickPinned(build)}
                    onTogglePin={() => void handleTogglePin(build)}
                  />
                ))}
              </List>
              <Divider />
              <SectionHeader label="All" />
            </>
          )}
          <List dense disablePadding>
            {bundleGroups.length === 0 && (
              <Box p={2}>
                <Typography variant="body2" color="text.secondary">
                  No matches.
                </Typography>
              </Box>
            )}
            {bundleGroups.map((group) => (
              <ListItemButton
                key={group.key}
                selected={activeBundle?.key === group.key}
                onClick={() => handlePickBundle(group)}
              >
                <Avatar
                  src={group.iconUrl}
                  variant="rounded"
                  sx={{ width: 28, height: 28, mr: 1.5, bgcolor: "grey.200" }}
                >
                  {group.label.charAt(0).toUpperCase()}
                </Avatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <PlatformIcon platform={group.platform} />
                      <Typography variant="body2" noWrap>
                        {group.label}
                      </Typography>
                    </Box>
                  }
                  secondary={group.subLabel}
                  secondaryTypographyProps={{ noWrap: true, variant: "caption" }}
                />
              </ListItemButton>
            ))}
          </List>
        </ColumnContainer>
        <ColumnContainer title="Version">
          <List dense disablePadding>
            {versionGroups.length === 0 && (
              <Box p={2}>
                <Typography variant="body2" color="text.secondary">
                  Select an app bundle.
                </Typography>
              </Box>
            )}
            {versionGroups.map((group) => (
              <ListItemButton
                key={group.key}
                selected={activeVersion?.key === group.key}
                onClick={() => handlePickVersion(group)}
              >
                <ListItemText
                  primary={group.label}
                  secondary={`${group.builds.length} build${
                    group.builds.length === 1 ? "" : "s"
                  }`}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true, variant: "caption" }}
                />
              </ListItemButton>
            ))}
          </List>
        </ColumnContainer>
        <ColumnContainer title="Build Number">
          <List dense disablePadding>
            {codeBuilds.length === 0 && (
              <Box p={2}>
                <Typography variant="body2" color="text.secondary">
                  Select a version.
                </Typography>
              </Box>
            )}
            {codeBuilds.map((build) => (
              <BuildCodeRow
                key={build.id}
                build={build}
                selected={values.selectedBuildId === build.id}
                pinned={pinnedSet.has(build.id)}
                onClick={() =>
                  void setFieldValue("selectedBuildId", build.id)
                }
                onTogglePin={() => void handleTogglePin(build)}
              />
            ))}
          </List>
        </ColumnContainer>
      </Box>
    </Box>
  );
};

const PlatformIcon = ({ platform }: { platform: string }) => {
  if (platform === "ios") {
    return <Apple sx={{ fontSize: 16, color: "text.secondary" }} />;
  }
  if (platform === "android") {
    return <Android sx={{ fontSize: 16, color: "text.secondary" }} />;
  }
  return null;
};

const ColumnContainer = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      borderRight: 1,
      borderColor: "divider",
      "&:last-of-type": { borderRight: 0 },
      minHeight: 0,
    }}
  >
    <Box
      sx={{
        px: 2,
        py: 1,
        bgcolor: "grey.50",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {title.toUpperCase()}
      </Typography>
    </Box>
    <Box sx={{ overflowY: "auto", flexGrow: 1 }}>{children}</Box>
  </Box>
);

const SectionHeader = ({ label }: { label: string }) => (
  <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: "uppercase", fontWeight: 600 }}
    >
      {label}
    </Typography>
  </Box>
);

const PinnedBuildRow = ({
  build,
  selected,
  onClick,
  onTogglePin,
}: {
  build: BuildData;
  selected: boolean;
  onClick: () => void;
  onTogglePin: () => void;
}) => {
  const buildType = getBuildTypeSuffix(build);
  const primary = `${getBundleLabel(build)}`;
  const secondary = [
    build.appVersionName,
    build.appVersionCode,
    buildType ? buildType.trim() : undefined,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <ListItemButton selected={selected} onClick={onClick}>
      <Avatar
        src={build.iconUrl}
        variant="rounded"
        sx={{ width: 24, height: 24, mr: 1.5, bgcolor: "grey.200" }}
      >
        {primary.charAt(0).toUpperCase()}
      </Avatar>
      <ListItemText
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <PlatformIcon platform={normalizePlatform(build.platform)} />
            <Typography variant="body2" noWrap>
              {primary}
            </Typography>
          </Box>
        }
        secondary={secondary}
        secondaryTypographyProps={{ noWrap: true, variant: "caption" }}
      />
      <Tooltip title="Unpin">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
        >
          <PushPin fontSize="small" />
        </IconButton>
      </Tooltip>
    </ListItemButton>
  );
};

const BuildCodeRow = ({
  build,
  selected,
  pinned,
  onClick,
  onTogglePin,
}: {
  build: BuildData;
  selected: boolean;
  pinned: boolean;
  onClick: () => void;
  onTogglePin: () => void;
}) => {
  const buildType = getBuildTypeSuffix(build);
  const uploaded = DateTime.fromMillis(build.epoch * 1000).toFormat(
    "d MMM yyyy, h:mm a"
  );
  const code = build.appVersionCode ?? "(no code)";
  return (
    <ListItemButton selected={selected} onClick={onClick}>
      <ListItemText
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" fontWeight={500} noWrap>
              {code}
            </Typography>
            <Chip
              size="small"
              label={`${getPlatformLabel(build.platform)}${buildType}`}
              variant="outlined"
              sx={{ height: 18, fontSize: 11 }}
            />
          </Box>
        }
        secondary={uploaded}
        secondaryTypographyProps={{ noWrap: true, variant: "caption" }}
      />
      <Tooltip title={pinned ? "Unpin" : "Pin"}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
        >
          {pinned ? (
            <PushPin fontSize="small" />
          ) : (
            <PushPinOutlined fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </ListItemButton>
  );
};

const RunCloudAlert = ({ handleClose }: { handleClose: () => void }) => (
  <>
    <Alert severity={"warning"}>
      <p>
        {"Runs will use devices configured in "}
        <Link
          to={"/gpt-driver/settings/devices"}
          className={"font-bold"}
          onClick={handleClose}
        >
          {"Settings > Devices > Cloud"}
        </Link>
      </p>
    </Alert>
    <Alert severity={"info"}>
      <p className={"text-[14px]"}>
        {"To trigger test runs programmatically, please refer to "}
        <Link
          target="_blank"
          to={
            "https://docs.mobileboost.io/gpt-driver-api/basics/images-and-media/ci-cd-examples"
          }
        >
          <span className="underline hover:cursor-pointer">
            our guide about CI/CD Integration
          </span>
        </Link>
        {" as well as "}
        <Link
          target="_blank"
          to={
            "https://docs.mobileboost.io/gpt-driver-api/basics/editor/api-examples"
          }
        >
          <span className="underline hover:cursor-pointer">
            our API Documentation.
          </span>
        </Link>
      </p>
    </Alert>
  </>
);

const buildDialogStateAtom = atomWithReset<{
  handleConfirm?: HandleConfirmRedirect | HandleConfirmRunTests;
  open: boolean;
  type?: "run" | "redirect";
  actionTitle: string;
}>({
  open: false,
  type: "redirect",
  actionTitle: "",
});

export { SelectBuildDialog, buildDialogStateAtom };
